import moment from 'moment-timezone';

// The anchor every historical DATETIME is stored in. All Showroompricing
// instances have run on Central time since day one, so naive stored values are
// Central wall-clock. This is only a fallback — the real value comes from the
// server's STORAGE_TIMEZONE config so an instance can correct it without a
// client release.
export const FALLBACK_TIMEZONE = 'America/Chicago';

// Formats we expect back from the API for DATETIME / DATETIME(6) columns. mysql2
// is configured with dateStrings, so these arrive as naive strings with no zone.
// ISO_8601 is included so already-offset-bearing values (if any endpoint sends
// them) are parsed at their true instant and merely re-displayed in the target
// zone.
const DTTM_FORMATS = [
  'YYYY-MM-DD HH:mm:ss.SSSSSS',
  'YYYY-MM-DD HH:mm:ss',
  moment.ISO_8601,
];

// Module-level active zones, kept in sync by TimezoneContextProvider. This lets
// formatDttm/formatDate be called from anywhere — plain functions, module-level
// helpers, components that don't consume the context — without threading a hook
// through every caller. The context is still the source of truth; this is just a
// mirror it pushes updates into.
// localStorage cache keys so first paint (before the context's async settings
// fetch resolves) already uses the right zones — avoids a Central→real flip and
// the spurious remount it would cause.
const CACHE_DEFAULT = 'tzDefault';
const CACHE_STORAGE = 'tzStorage';

const _readLS = (key) => {
  try {
    return localStorage.getItem(key) || null;
  } catch (e) {
    return null;
  }
};

// Seed effective zone from the persisted user, else the cached instance default,
// else the fallback. Storage anchor seeds from its cache, else the fallback.
const _initEffectiveZone = () => {
  try {
    const u = JSON.parse(localStorage.getItem('userDetails') || 'null');
    if (u && u.timezone) return u.timezone;
  } catch (e) {
    // ignore malformed localStorage
  }
  return _readLS(CACHE_DEFAULT) || FALLBACK_TIMEZONE;
};

let _zones = {
  storageZone: _readLS(CACHE_STORAGE) || FALLBACK_TIMEZONE,
  effectiveZone: _initEffectiveZone(),
};

// Subscribers (React components) re-render when the active zones change, so
// timestamps already on screen update without needing a navigation/remount.
let _version = 0;
const _listeners = new Set();

export const subscribeZones = (fn) => {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
};

export const getZoneVersion = () => _version;

export const setActiveZones = ({ storageZone, effectiveZone } = {}) => {
  let changed = false;
  if (storageZone && storageZone !== _zones.storageZone) {
    _zones.storageZone = storageZone;
    changed = true;
  }
  if (effectiveZone && effectiveZone !== _zones.effectiveZone) {
    _zones.effectiveZone = effectiveZone;
    changed = true;
  }
  if (changed) {
    _version += 1;
    _listeners.forEach((fn) => fn(_version));
  }
};

export const getActiveZones = () => _zones;

/**
 * Render a stored DATETIME in the user's effective zone WITHOUT misrepresenting it.
 *
 * Stored values carry no offset, so we pin the source zone with
 * moment.tz(value, storageZone) instead of letting `new Date()` reinterpret the
 * string as browser-local time (the previous, subtly-wrong behavior). Central
 * data written in July is CDT (UTC-5), in January is CST (UTC-6); moment applies
 * the correct per-date offset automatically because we pass the IANA name.
 *
 * displayZone/storageZone default to the active zones set by the context, so
 * most callers pass only the value (and optionally a format).
 */
export const formatDttm = (
  value,
  {
    displayZone = _zones.effectiveZone,
    storageZone = _zones.storageZone,
    format = 'MM/DD/YYYY h:mm A',
    fallback = '—',
  } = {}
) => {
  if (value === null || value === undefined || value === '') return fallback;
  const m = moment.tz(value, DTTM_FORMATS, storageZone);
  if (!m.isValid()) return fallback;
  return m.tz(displayZone || storageZone).format(format);
};

// Convert a stored DATETIME to its true epoch ms (interpreting it in the storage
// zone), for elapsed-time / "ago" math. Returns NaN for empty/invalid input.
export const dttmToEpoch = (value, { storageZone = _zones.storageZone } = {}) => {
  if (value === null || value === undefined || value === '') return NaN;
  return moment.tz(value, DTTM_FORMATS, storageZone).valueOf();
};

/**
 * Calendar-date rendering for DATE columns (no time component). Deliberately
 * does NOT shift by timezone — a pure date has no instant, and converting it
 * could roll it to the previous/next day.
 */
export const formatDate = (
  value,
  { format = 'MM/DD/YYYY', fallback = '—' } = {}
) => {
  if (value === null || value === undefined || value === '') return fallback;
  const m = moment(value, ['YYYY-MM-DD', moment.ISO_8601]);
  return m.isValid() ? m.format(format) : fallback;
};

// A short, ordered list for the picker's top section; the full IANA list is
// available via moment.tz.names() for the "all zones" fallback.
export const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Mountain — no DST (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
];

export const ALL_TIMEZONES = moment.tz.names();

// Human label for a zone including its current UTC offset, e.g.
// "Central (Chicago) — UTC-05:00". Uses the browser's current instant only to
// show the offset; storage/display math never relies on this.
export const zoneLabel = (zone) => {
  const known = COMMON_TIMEZONES.find((z) => z.value === zone);
  const base = known ? known.label : zone;
  try {
    const offset = moment.tz(zone).format('Z');
    return `${base} — UTC${offset}`;
  } catch (e) {
    return base;
  }
};
